/**
 * ProfessionalRoutes.jsx — clinician dashboard routes.
 * Shaped by: G2f owner 2026-10-12.
 * Invariants: /health-records is the clinician's own MasterChart hub and
 *   follows the launcher catalog (clinicians are No). The ads desk stays mounted.
 * Related: RequireLauncherApp.jsx, HealthRecordsHub.jsx.
 */
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import UpgradePage from "pages/dashboard/UpgradePage";
import ApprovalPage from "pages/dashboard/global-admin/ApprovalPage";
import AssistantManagerPage from "pages/dashboard/global-admin/AssistantManagerPage";
import UsageMonitorPage from "pages/dashboard/global-admin/UsageMonitorPage";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import DashboardLayout from "components/dashboard/DashboardLayout";
import Settings from "components/dashboard/Settings";
import ProfessionalOnboardingDialog from "components/dashboard/ProfessionalOnboardingDialog";
import HealthRecords from "components/dashboard/HealthRecords";
import { db } from "services/firebase";
import { PromptsManagerProvider } from "context/PromptsManager";
import { ChartMindSessionProvider } from "context/ChartMindSession";
import PeerViewPage from "pages/dashboard/professional/PeerViewPage";
import ChartMindPlusPage from "pages/chartmindPlus/ChartMindPlusPage";
import RequireLauncherApp from "components/appLauncher/RequireLauncherApp";
import ChartMindPlusDifferentialPage from "pages/chartmindPlus/ChartMindPlusDifferentialPage";
import RetiredChartMindRoute from "components/common/RetiredChartMindRoute";
import ClinicianHomeRedirect from "routes/ClinicianHomeRedirect";
import DrugReferenceDashboard from "features/drugReference/components/DrugReferenceDashboard";
import DrugDetail from "features/drugReference/components/DrugDetail";
import DrugReviewQueue from "features/drugReference/components/DrugReviewQueue";
import ZipSlotAdsGate from "components/common/ZipSlotAdsGate";
import ZipSlotBiddingPage from "pages/dashboard/zipSlotAds/ZipSlotBiddingPage";
import AdBalancePage from "pages/dashboard/zipSlotAds/AdBalancePage";
import AdPerformancePage from "pages/dashboard/zipSlotAds/AdPerformancePage";
// [BETA-KIJABE] Import for Kijabe beta route — remove when beta period ends
import AskMeAnythingPage from "pages/AskMeAnythingPage";

const ProfessionalRoutes = () => {
  const { user, userData, subscription, isGlobalAdmin } = useAuth();
  const location = useLocation();
  const [showProfessionalOnboarding, setShowProfessionalOnboarding] =
    useState(false);

  // [BETA-KIJABE] Temporary flag for Kijabe beta testers — remove when beta period ends
  const isBetaKijabe = Boolean(userData?.betaAccess);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await db.collection("users").doc(user.uid).get();
          const userData = userDoc.data();

          if (
            userData?.role === "professional" &&
            !userData?.hideOnboardingDialog
          ) {
            setShowProfessionalOnboarding(true);
          }
        } catch (error) {
          console.error("Error checking onboarding status:", error);
        }
      }
    };

    checkOnboardingStatus();
  }, [user]);

  // Full-width for chat surfaces and ChartMind editor (not the session list)
  const isChatRoute = () => {
    const path = location.pathname;
    const isChartMindSessionList = /\/chartmind\/sessions\/?$/.test(path);
    return (
      path === "/dashboard" ||
      path === "/dashboard/" ||
      path.includes("/peerview") ||
      // [BETA-KIJABE] Full-width for Kijabe AMA — remove when beta period ends
      path.includes("/ask-me-anything") ||
      (path.includes("/chartmind") && !isChartMindSessionList)
    );
  };

  return (
    <DashboardLayout fullWidth={isChatRoute()}>
      <ProfessionalOnboardingDialog
        open={showProfessionalOnboarding}
        onClose={() => setShowProfessionalOnboarding(false)}
        userId={user?.uid}
        userData={userData}
      />

      <PromptsManagerProvider>
        {/*
          ChartMindSessionProvider mounts above <Routes> so the in-progress encounter
          survives navigation (e.g. user goes to ChatPage and comes back). State is RAM-only.
        */}
        <ChartMindSessionProvider>
        <Routes>
          {/* Prompt 27: ChartMind v1 home is ClinicalHUD2 or the Apps launcher, never the retired page. */}
          <Route path="/" element={<ClinicianHomeRedirect />} />
          <Route path="/peerview" element={<PeerViewPage />} />
          <Route path="/drug-reference" element={<DrugReferenceDashboard />} />
          <Route path="/drug-reference/drug/:drugId" element={<DrugDetail />} />
          <Route path="/drug-reference/review" element={<DrugReviewQueue />} />
          {/* [BETA-KIJABE] Kijabe-only AMA route — remove when beta period ends */}
          <Route
            path="/ask-me-anything"
            element={
              isBetaKijabe
                ? <AskMeAnythingPage isDashboard />
                : <Navigate to="/dashboard" replace />
            }
          />
          {/* Session lives in ChartMindSessionProvider above — no key= prop, no disableSave prop. */}
          <Route path="/chartmind" element={<RetiredChartMindRoute />} />
          <Route
            path="/chartmind-plus"
            element={
              <RequireLauncherApp appId="chartmind-plus">
                <ChartMindPlusPage />
              </RequireLauncherApp>
            }
          />
          <Route
            path="/chartmind-plus/differential"
            element={
              <RequireLauncherApp appId="chartmind-plus">
                <ChartMindPlusDifferentialPage />
              </RequireLauncherApp>
            }
          />
          {/*
            "Open in ChartMind" deep-link target used by the CareStart/VoxPont/CareCompris
            and SavoirCare entry points (they navigate to /dashboard/chartmind/recording to
            start charting a just-finished encounter). Must precede /chartmind/:sessionId:
            without this, "recording" is captured as :sessionId and ChartMindPage calls
            loadSession("recording") on a nonexistent chartmind/recording doc. Redirect to
            the canonical base route, which opens a fresh session at the Record step.
            (Passing patient context / forcing a new encounter is a separate follow-up.)
          */}
          <Route
            path="/chartmind/recording"
            element={<RetiredChartMindRoute />}
          />
          {/* [BETA-KIJABE] Beta users can't access session history — redirect to base — remove when beta ends */}
          <Route
            path="/chartmind/sessions"
            element={<RetiredChartMindRoute />}
          />
          {/* [BETA-KIJABE] Beta users can't load saved sessions — redirect to base — remove when beta ends */}
          <Route
            path="/chartmind/:sessionId"
            element={<RetiredChartMindRoute />}
          />

          <Route
            path="/settings"
            element={<Settings uid={user?.uid} subscription={subscription} />}
          />
          <Route
            path="/advertising/slots"
            element={
              <ZipSlotAdsGate>
                <ZipSlotBiddingPage />
              </ZipSlotAdsGate>
            }
          />
          <Route
            path="/advertising/balance"
            element={
              <ZipSlotAdsGate>
                <AdBalancePage />
              </ZipSlotAdsGate>
            }
          />
          <Route
            path="/advertising/performance"
            element={
              <ZipSlotAdsGate>
                <AdPerformancePage />
              </ZipSlotAdsGate>
            }
          />
          <Route
            path="/health-records"
            element={
              <RequireLauncherApp appId="masterchart">
                <HealthRecords />
              </RequireLauncherApp>
            }
          />

          {/* [BETA-KIJABE] Upgrade route hidden for Kijabe beta users — remove condition when beta ends */}
          {!isBetaKijabe && !userData?.dailyPassExpiresAt && (
            <Route path="/upgrade" element={<UpgradePage />} />
          )}

          {/* Global-admin-only routes */}
          <Route
            path="/admin/approval"
            element={
              isGlobalAdmin ? (
                <ApprovalPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/assistants"
            element={
              isGlobalAdmin ? (
                <AssistantManagerPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/usage"
            element={
              isGlobalAdmin ? (
                <UsageMonitorPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/prompts"
            element={
              isGlobalAdmin ? (
                <PromptsManagerPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
        </ChartMindSessionProvider>
      </PromptsManagerProvider>
    </DashboardLayout>
  );
};

export default ProfessionalRoutes;
